import { MembershipLevel } from './membership';
import { LineItem } from './order';

export interface Invoice {
  id: string;
  created: {
    formatted: string;
  };
  updated: {
    formatted: string;
  };
  subscriptionId: string;
  time: number;
  provider: string;
  providerCustomerId: string;
  providerInvoiceId: string;
  providerEventType: string;
  providerStatus: string;
  status: string;
  amountDue: number;
  amountPaid: number;
  attemptCount: number;
  attempted: number;
  paid: boolean;
  billingReason: string;
  hostedInvoiceUrl: string;
  period: SubscriptionPeriod;
  periodStart: {
    formatted: string;
  };
  periodEnd: {
    formatted: string;
  };
  currency: string;
  tax: number;
  total: number;
  couponCode?: string;
  discountAmount?: number;
}

export interface Contract {
  minCharge: number;
  minUsage: number;
  periodsInTerm: number;
  description: string;
  discountPercent: number;
  featureSkus: string[];
  renewalDate: {
    formatted: string;
  };
  canceledDate: {
    formatted: string;
  };
  cancelAtRenewalDate: boolean;
  contractTerms: string;
  acceptedAt: {
    formatted: string;
  };
}

export type SubscriptionPeriod = 'month' | 'year';
export type SubscriptionProvider = 'google' | 'apple' | 'stripe' | 'unknown';

type CouponAtSubscriptionCreation = {
  id: string;
  couponCode: string;
  couponDescription: string;
  couponNumberOfDays: number;
  discountAmount: number;
};

export interface SubscriptionData {
  id: string;
  paymentIntentId: string;
  status: string; // "active", "inactive", "trialing"
  provider: SubscriptionProvider;
  currentPeriodEnd: {
    formatted: string;
  };
  paidUntilDate: {
    formatted: string;
  };
  subscriptionId: string;
  cancelAtPeriodEnd: boolean;
  lineItems: LineItem[];
  created: {
    formatted: string;
  };
  updated: {
    formatted: string;
  };
  confirmationCode: string;
  invoices: Invoice[];
  providerPaymentMethodId: string;
  trialDaysOnHeadsetConnect?: number;
  headsetConnectedTrialActivated?: boolean;
  discountAmount?: number;
  couponCode?: string;
  couponNumberOfDays?: number;
  level?: MembershipLevel;
  orderId: string;
  period: SubscriptionPeriod;
  price: number;
  currency: string;
  couponAtSubscriptionCreation: CouponAtSubscriptionCreation;
  contracts?: Contract[];
  nextInvoiceLineItems: LineItem[];
}

export class Subscription {
  id: string;
  paymentIntentId: string;
  status: string; // "active", "inactive", "trialing"
  provider: SubscriptionProvider;
  currentPeriodEnd: Date;
  paidUntilDate: Date;
  updated: Date;
  subscriptionId: string;
  cancelAtPeriodEnd: boolean;
  lineItems: LineItem[];
  nextInvoiceLineItems: LineItem[];
  created: Date;
  confirmationCode: string;
  invoices: Invoice[];
  providerPaymentMethodId: string;
  trialDaysOnHeadsetConnect?: number;
  headsetConnectedTrialActivated?: boolean;
  discountAmount?: number;
  couponCode?: string;
  couponNumberOfDays?: number;
  level?: MembershipLevel;
  orderId: string;
  period: SubscriptionPeriod;
  price: number;
  currency: string;
  couponAtSubscriptionCreation: CouponAtSubscriptionCreation;
  contracts: Contract[];

  constructor(props: SubscriptionData) {
    this.id = props.id;
    this.status = props.status;
    this.provider = props.provider;
    this.paymentIntentId = props.paymentIntentId;
    this.currentPeriodEnd = new Date(props.currentPeriodEnd.formatted);
    this.paidUntilDate = new Date(props.paidUntilDate.formatted);
    this.updated = new Date(props.updated.formatted);
    this.subscriptionId = props.subscriptionId;
    this.cancelAtPeriodEnd = props.cancelAtPeriodEnd;
    this.lineItems = props.lineItems;
    this.created = new Date(props.created.formatted);
    this.confirmationCode = props.confirmationCode;
    this.invoices = props.invoices.map((i) => ({
      ...i,
      subscriptionId: props.subscriptionId,
      period: props.period
    }));

    this.providerPaymentMethodId = props.providerPaymentMethodId;
    this.trialDaysOnHeadsetConnect = props.trialDaysOnHeadsetConnect;
    this.discountAmount = props.discountAmount;
    this.couponCode = props.couponCode;
    this.couponNumberOfDays = props.couponNumberOfDays;
    this.level = props.level;
    this.orderId = props.orderId;
    this.period = props.period;
    this.price = props.price;
    this.currency = props.currency;
    this.couponAtSubscriptionCreation = props.couponAtSubscriptionCreation;
    this.contracts = props.contracts ?? [];
    this.nextInvoiceLineItems = props.nextInvoiceLineItems ?? [];
  }

  isCanceled() {
    return this.status === 'canceled';
  }

  isCancelPending() {
    return (
      this.cancelAtPeriodEnd ||
      (this.contracts.length > 0 && this.contracts[0].cancelAtRenewalDate)
    );
  }

  isProfesssional() {
    return this.level === 'pro';
  }

  isPersonal() {
    return this.level === 'member';
  }

  formatStatus = (): string => {
    if (this.cancelAtPeriodEnd) {
      return 'Canceled';
    }

    switch (this.status) {
      case 'active':
        return 'Active';
      case 'incomplete':
        return 'Incomplete';
      case 'incomplete_expired':
        return 'Incompelete / Expired';
      case 'trialing':
        return 'Free Trial Period';
      case 'past_due':
        return 'Past Due';
      case 'unpaid':
        return 'Payment Required';
      default:
        return this.status;
    }
  };

  isStripe = () => this.provider === 'stripe';
  isApple = () => this.provider === 'apple';
  isGoogle = () => this.provider === 'google';
  isActive = () => this.status === 'active';

  isTrialPendingHeadsetRegistration(): boolean {
    return (
      !!this.trialDaysOnHeadsetConnect &&
      this.trialDaysOnHeadsetConnect > 0 &&
      !this.headsetConnectedTrialActivated
    );
  }
}
